<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-gray-50 text-gray-900">
    
    <!-- Studio Header -->
    <header class="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0 z-50">
      <div class="flex items-center gap-3">
        <el-button circle text @click="toggleDataPanel" :type="(isMobile && showMobileDrawer && mobileActiveTab === 'data') || (!isMobile && showDataPanel) ? 'primary' : ''" :plain="(isMobile && showMobileDrawer && mobileActiveTab === 'data') || (!isMobile && showDataPanel)">
          <el-icon :size="18"><Menu /></el-icon>
        </el-button>
        
        <div class="flex items-center gap-3 ml-2 border-l border-gray-200 pl-4 h-6">
           <div class="flex items-center justify-center w-8 h-8 rounded bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold">W</div>
           <span class="font-bold text-lg tracking-tight hidden md:inline">WordCloud<span class="text-indigo-600">Studio</span></span>
        </div>
      </div>

      <div class="flex items-center gap-2">
         <el-button circle text @click="toggleOptionsPanel" :type="(isMobile && showMobileDrawer && mobileActiveTab === 'options') || (!isMobile && showOptionsPanel) ? 'primary' : ''" :plain="(isMobile && showMobileDrawer && mobileActiveTab === 'options') || (!isMobile && showOptionsPanel)">
           <el-icon :size="18"><Setting /></el-icon>
         </el-button>
      </div>
    </header>

    <!-- Main Workspace -->
    <div class="flex-1 flex overflow-hidden relative">
      
      <!-- Left Panel (Desktop Only) -->
      <transition name="slide-left">
        <aside 
          v-if="!isMobile"
          v-show="showDataPanel"
          class="bg-white border-r border-gray-200 flex flex-col z-40 w-80 relative transition-all duration-300"
        >
           <LeftSidebar 
            :isMobile="false"
            v-model:textToAnalyze="textToAnalyze"
            :processedChartData="processedChartData"
            :loading="loading"
            :tableKey="tableKey"
            @analyze-text="analyzeText"
            @add-data="addData"
            @delete-data="deleteData"
            @update-data="updateData"
            @change-lang="changeLang"
            @price-click="priceVisible = true"
            @open-service="serviceVisible = true"
            @clear-data="chartData = []"
           />
        </aside>
      </transition>

      <!-- Center Canvas (Artboard) -->
      <main class="flex-1 bg-gray-100 bg-dot-pattern relative overflow-hidden flex flex-col items-center justify-center">
        
        <!-- Zoom/Toolbar Floating Pill -->
        <div class="absolute top-4 z-20 bg-white/90 backdrop-blur rounded-full shadow-sm border border-gray-200 px-3 py-1.5 flex items-center gap-2">
           <el-button circle text size="small" @click="zoomOut"><el-icon><Remove /></el-icon></el-button>
           <span class="text-xs font-mono w-12 text-center text-gray-500">{{ Math.round(zoomLevel * 100) }}%</span>
           <el-button circle text size="small" @click="zoomIn"><el-icon><Plus /></el-icon></el-button>
           <div class="w-px h-3 bg-gray-300 mx-1"></div>
           <el-button circle text size="small" @click="resetZoom" :title="$t('Reset View')"><el-icon><Refresh /></el-icon></el-button>
        </div>

        <!-- The Artboard -->
        <div 
          class="relative transition-transform duration-200 ease-out shadow-lg bg-white"
          :style="artboardStyle"
        >
           <!-- Loading Overlay -->
           <div v-if="loading" class="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center">
              <div class="flex flex-col items-center">
                 <div class="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                 <span class="text-xs font-semibold text-indigo-600">Generating...</span>
              </div>
           </div>

           <WordCloudChart
             ref="wordCloud"
             :isSubscribed="isSubscribed"
             :isMobile="isMobile"
             :chartData="chartData"
             :maskSrc="currentMaskSrc"
             :font="selectedFont"
             :backgroundColor="backgroundColor"
             :width="imageWidth"
             :height="imageHeight"
             :minSize="minSize"
             :maxSize="maxSize"
             :carouselImages="imageUrls"
           />
        </div>
        
      </main>

      <!-- Right Panel (Desktop Only) -->
      <transition name="slide-right">
        <aside 
          v-if="!isMobile"
          v-show="showOptionsPanel"
          class="bg-white border-l border-gray-200 flex flex-col z-40 w-80 relative transition-all duration-300"
        >
          <RightSidebar
            :isMobile="false"
            v-model:selectedFont="selectedFont"
            :fontOptions="fontOptions"
            v-model:backgroundColor="backgroundColor"
            :predefineColors="predefineColors"
            :selectedSize="selectedSize"
            :defaultSizes="defaultSizes"
            v-model:imageWidth="imageWidth"
            v-model:imageHeight="imageHeight"
            @update-chart="handleUpdateChart"
            @export-layout="exportLayout"
            @open-mask-dialog="maskVisible = true"
            @mask-file-selected="handleMaskFileSelect"
            @update-image-size="updateImageSize"
          />
        </aside>
      </transition>

      <!-- Unified Mobile Drawer -->
      <transition name="slide-up">
        <div v-if="isMobile && showMobileDrawer" class="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-xl shadow-2xl flex flex-col h-[70vh] border-t border-gray-200">
           
           <!-- Mobile Drawer Header -->
           <div class="h-12 border-b border-gray-100 flex items-center px-4 bg-gray-50 rounded-t-xl shrink-0">
              <div class="flex-1 flex justify-center gap-1 bg-gray-200/50 p-1 rounded-lg">
                  <button 
                    class="flex-1 py-1 px-3 text-xs font-medium rounded-md transition-all"
                    :class="mobileActiveTab === 'data' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'"
                    @click="mobileActiveTab = 'data'"
                  >
                    {{ $t('Data') }}
                  </button>
                  <button 
                    class="flex-1 py-1 px-3 text-xs font-medium rounded-md transition-all"
                    :class="mobileActiveTab === 'options' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'"
                    @click="mobileActiveTab = 'options'"
                  >
                    {{ $t('Style') }}
                  </button>
              </div>
              <el-button circle text class="ml-3" @click="showMobileDrawer = false">
                 <el-icon><Close /></el-icon>
              </el-button>
           </div>
           
           <!-- Content Area -->
           <div class="flex-1 overflow-hidden relative">
              <div v-if="mobileActiveTab === 'data'" class="h-full">
                  <LeftSidebar 
                    :isMobile="true"
                    :inDrawer="true"
                    v-model:textToAnalyze="textToAnalyze"
                    :processedChartData="processedChartData"
                    :loading="loading"
                    :tableKey="tableKey"
                    @analyze-text="analyzeText"
                    @add-data="addData"
                    @delete-data="deleteData"
                    @update-data="updateData"
                    @change-lang="changeLang"
                    @price-click="priceVisible = true"
                    @open-service="serviceVisible = true"
                    @close-mobile="showMobileDrawer = false"
                    @clear-data="chartData = []"
                   />
              </div>
              <div v-if="mobileActiveTab === 'options'" class="h-full">
                  <RightSidebar
                    :isMobile="true"
                    :inDrawer="true"
                    v-model:selectedFont="selectedFont"
                    :fontOptions="fontOptions"
                    v-model:backgroundColor="backgroundColor"
                    :predefineColors="predefineColors"
                    :selectedSize="selectedSize"
                    :defaultSizes="defaultSizes"
                    v-model:imageWidth="imageWidth"
                    v-model:imageHeight="imageHeight"
                    @update-chart="handleUpdateChart"
                    @export-layout="exportLayout"
                    @open-mask-dialog="maskVisible = true"
                    @mask-file-selected="handleMaskFileSelect"
                    @update-image-size="updateImageSize"
                    @close-mobile="showMobileDrawer = false"
                  />
              </div>
           </div>
        </div>
      </transition>

      <!-- Mobile Overlay Backdrop -->
      <div v-if="isMobile && showMobileDrawer" class="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" @click="showMobileDrawer = false"></div>

    </div>

    <!-- Dialogs (Keep functional) -->
    <el-dialog v-model="maskVisible" :title="$t('Select Shape')" :width="dialogWidth" append-to-body destroy-on-close>
       <div v-if="isSubscribed">
          <SelectMask @mask-selected="handleMaskSelected" />
       </div>
       <div v-else>
         <!-- VIP PROMO -->
         <div class="text-center p-8">
            <el-icon :size="48" class="text-indigo-500 mb-4"><PriceTag /></el-icon>
             <h3 class="text-lg font-bold mb-2">{{ $t('Premium Feature') }}</h3>
             <p class="text-gray-500 mb-6">{{ $t('Unlock custom masks and more with Premium.') }}</p>
             <el-button type="primary" size="large" @click="priceVisible = true">{{ $t('View Plans') }}</el-button>
         </div>
       </div>
    </el-dialog>

    <el-dialog 
      v-model="priceVisible" 
      :show-close="false"
      :width="priceDialogWidth" 
      append-to-body 
      class="clean-dialog"
    >
        <div class="relative">
           <el-button 
             class="absolute -top-2 right-0 z-20" 
             text 
             circle 
             @click="dismissPricing"
           >
             <el-icon :size="20"><Close /></el-icon>
           </el-button>
           <el-tabs class="custom-tabs">
             <el-tab-pane :label="$t('Plans')"><PricePage /></el-tab-pane>
             <el-tab-pane :label="$t('My Account')"><Account @subscription-updated="checkSubscription" /></el-tab-pane>
           </el-tabs>
        </div>
    </el-dialog>

    <el-dialog v-model="serviceVisible" :title="$t('Customer Service')" :width="serviceDialogWidth" append-to-body>
      <Service />
    </el-dialog>

  </div>
</template>

<script>
import { 
  ElButton, ElIcon, ElDialog, ElTabs, ElTabPane 
} from 'element-plus';
import { 
  Menu, Setting, Remove, Plus, Refresh, PriceTag, Close 
} from '@element-plus/icons-vue';
import { ref } from 'vue';

import LeftSidebar from './home/LeftSidebar.vue';
import RightSidebar from './home/RightSidebar.vue';
import WordCloudChart from './home/WordCloudChart.vue';
import SelectMask from './SelectMask.vue';
import PricePage from './PricePage.vue';
import Service from './Service.vue';
import Account from './Account.vue';

import dogImg from '../assets/love.png';
import { CONFIG_GOOGLE_FONTS } from '@/config';

export default {
  name: 'HomePage',
  components: {
    ElButton, ElIcon, ElDialog, ElTabs, ElTabPane,
    Menu, Setting, Remove, Plus, Refresh, PriceTag, Close,
    LeftSidebar, RightSidebar, WordCloudChart, SelectMask, PricePage, Service, Account
  },
  setup() {
    return { 
      predefineColors: ref(['#4f46e5', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#111827', '#ffffff']) 
    };
  },
  data() {
    return {
      // Core Data
      chartData: [],
      textToAnalyze: '',
      loading: false,
      
      // Configuration
      backgroundColor: '#FFFFFF',
      imageWidth: 500,
      imageHeight: 500,
      minSize: 10, 
      maxSize: 180,
      selectedFont: 'Roboto',
      fontOptions: CONFIG_GOOGLE_FONTS,
      selectedSize: null,
      defaultSizes: [
        { width: 800, height: 600, tag: "800x600" },
        { width: 1024, height: 768, tag: "1024x768" },
        { width: 1920, height: 1080, tag: "1080p" },
        { width: 500, height: 500, tag: "Square (S)" },
        { width: 1000, height: 1000, tag: "Square (M)" },
      ],
      imageUrls: ['cat.png', 'flight.png', 'flower.png'],

      // Masks
      selectedMask: null,
      customMaskDataUrl: null,

      // UI State
      isMobile: false,
      showDataPanel: true, 
      showOptionsPanel: true,
      
      // Mobile Unified Drawer
      showMobileDrawer: false,
      mobileActiveTab: 'data', // 'data' | 'options'

      zoomLevel: 1,
      isSubscribed: false,

      // Dialogs
      maskVisible: false,
      priceVisible: false,
      serviceVisible: false,
    };
  },
  computed: {
    processedChartData() { return this.chartData.slice(); },
    currentMaskSrc() { return this.customMaskDataUrl || this.selectedMask || dogImg; },
    artboardStyle() {
      // Artboard size is physically set by imageWidth/Height, scaled by CSS transform for view
      return {
        width: `${this.imageWidth}px`,
        height: `${this.imageHeight}px`,
        transform: `scale(${this.zoomLevel})`,
        transformOrigin: 'center center'
      };
    },
    // Responsive dialog widths
    dialogWidth() {
      return this.isMobile ? '95%' : '500px';
    },
    priceDialogWidth() {
      return this.isMobile ? '95%' : '600px';
    },
    serviceDialogWidth() {
      return this.isMobile ? '95%' : '400px';
    }
  },
  created() {
    this.checkMobile();
    if (this.isMobile) {
      this.showDataPanel = false;
      this.showOptionsPanel = false;
    }
    window.addEventListener('resize', this.checkMobile);
    this.$watch('selectedFont', (f) => this.loadSelectedFont(f));
  },
  mounted() {
    this.$i18n.locale = navigator.language; 
    this.generateData(); 
    this.checkSubscription();
    this.loadSelectedFont(this.selectedFont);
    
    // Show pricing dialog for non-subscribed users unless they dismissed it
    const pricingDismissed = localStorage.getItem('pricingDismissed');
    if (!this.isSubscribed && !pricingDismissed) {
      // Show pricing dialog after a short delay for better UX
      setTimeout(() => {
        this.priceVisible = true;
      }, 1000);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      // Increased breakpoint to 1024px to cover iPad/Tablets in portrait
      this.isMobile = window.innerWidth <= 1024;
    },
    toggleDataPanel() { 
        if (this.isMobile) {
            this.mobileActiveTab = 'data';
            this.showMobileDrawer = true;
        } else {
            this.showDataPanel = !this.showDataPanel; 
        }
    },
    toggleOptionsPanel() { 
        if (this.isMobile) {
            this.mobileActiveTab = 'options';
            this.showMobileDrawer = true;
        } else {
            this.showOptionsPanel = !this.showOptionsPanel; 
        }
    },
    
    // Zoom
    zoomIn() { this.zoomLevel = Math.min(this.zoomLevel * 1.1, 3); },
    zoomOut() { this.zoomLevel = Math.max(this.zoomLevel / 1.1, 0.1); },
    resetZoom() { 
      // Auto-fit logic could go here, for now 100%
      this.zoomLevel = 1; 
    },

    // Logic Wrappers
    generateData() {
      // Mock data
      this.chartData = [
         { name: "🌼", value: 1800 },
         { name: "Design", value: 1200 },
         { name: "Creative", value: 1100 },
         { name: "Studio", value: 1000 },
         { name: "Visualization", value: 950 },
         { name: "Art", value: 900 },
         { name: "Code", value: 850 },
         { name: "Future", value: 800 },
         { name: "Innovation", value: 780 },
         { name: "Data", value: 750 },
         { name: "Cloud", value: 720 },
         { name: "Tech", value: 700 },
         { name: "Growth", value: 680 },
         { name: "Vision", value: 650 },
         { name: "Smart", value: 620 },
         { name: "AI", value: 600 },
         { name: "Web", value: 580 },
         { name: "Mobile", value: 560 },
         { name: "App", value: 540 },
         { name: "UI", value: 520 },
         { name: "UX", value: 500 },
         { name: "Global", value: 480 },
         { name: "Fast", value: 460 },
         { name: "Secure", value: 440 },
         { name: "Style", value: 420 },
         { name: "Modern", value: 400 },
         { name: "Clean", value: 380 },
         { name: "Power", value: 360 },
         { name: "Energy", value: 350 },
         { name: "Concept", value: 340 },
         { name: "Idea", value: 330 },
         { name: "Inspire", value: 320 },
         { name: "Dream", value: 310 },
         { name: "Create", value: 300 },
         { name: "Build", value: 290 },
         { name: "Launch", value: 280 },
         { name: "Scale", value: 270 },
         { name: "Impact", value: 260 },
         { name: "Value", value: 250 },
         { name: "Focus", value: 240 },
         { name: "Team", value: 230 },
         { name: "Work", value: 220 },
         { name: "Life", value: 210 },
         { name: "Color", value: 200 },
         { name: "Shape", value: 195 },
         { name: "Form", value: 190 },
         { name: "Line", value: 185 },
         { name: "Space", value: 180 },
         { name: "Texture", value: 175 },
         { name: "Pattern", value: 170 },
         { name: "Balance", value: 165 },
         { name: "Harmony", value: 160 },
         { name: "Rhythm", value: 155 },
         { name: "Contrast", value: 150 },
         { name: "Light", value: 145 },
         { name: "Shadow", value: 140 },
         { name: "Vibrant", value: 135 },
         { name: "Bold", value: 130 },
         { name: "Dynamic", value: 125 },
         { name: "Fluid", value: 120 },
         { name: "Organic", value: 115 },
         { name: "Geometry", value: 110 },
         { name: "Digital", value: 105 },
         { name: "Analog", value: 100 },
         { name: "Pixel", value: 95 },
         { name: "Vector", value: 90 },
         { name: "Raster", value: 85 },
         { name: "Canvas", value: 80 },
         { name: "Brush", value: 75 },
         { name: "Ink", value: 70 },
         { name: "Paper", value: 65 },
         { name: "Print", value: 60 },
         { name: "Screen", value: 55 },
         { name: "Touch", value: 50 },
         { name: "Mouse", value: 45 },
         { name: "Keyboard", value: 40 },
         { name: "Code", value: 35 },
         { name: "Logic", value: 30 },
         { name: "System", value: 25 },
         { name: "Network", value: 24 },
         { name: "Connect", value: 23 },
         { name: "Share", value: 22 },
         { name: "Like", value: 21 },
         { name: "Follow", value: 20 },
         { name: "Trend", value: 19 },
         { name: "Viral", value: 18 },
         { name: "Social", value: 17 },
         { name: "Media", value: 16 },
         { name: "Content", value: 15 },
         { name: "Story", value: 14 },
         { name: "Brand", value: 13 },
         { name: "Identity", value: 12 },
         { name: "Logo", value: 11 },
         { name: "Icon", value: 10 },
         { name: "Image", value: 9 },
         { name: "Photo", value: 8 },
         { name: "Video", value: 7 },
         { name: "Audio", value: 6 },
         { name: "Music", value: 5 },
         { name: "Sound", value: 4 },
         { name: "Noise", value: 3 },
         { name: "Silence", value: 2 },
         { name: "Peace", value: 1 }
      ];    
      },
    addData(item) { this.chartData.push({ name: item.key, value: Number(item.value) }); },
    deleteData(idx) { this.chartData.splice(idx, 1); },
    updateData({ index, field, value }) {
      if (this.chartData[index]) {
        if (field === 'name') {
          this.chartData[index].name = value;
        } else if (field === 'value') {
          this.chartData[index].value = Number(value);
        }
      }
    },
    analyzeText() {
       if (!this.checkSubscription()) return;
       // ... simplify for brevity, assumed implementation exists or provided by mixin/logic
       const words = this.textToAnalyze.split(/\s+/);
       const map = {};
       words.forEach(w => w && (map[w] = (map[w] || 0) + 1));
       this.chartData = Object.entries(map).map(([k,v]) => ({ name: k, value: v }));
    },
    checkSubscription() {
       const status = localStorage.getItem("purchaseStatus");
       this.isSubscribed = (status === "purchased");
       if (!this.isSubscribed) {
           // Maybe auto-show price? 
           // this.priceVisible = true;
       }
       return this.isSubscribed;
    },
    
    // Pass-throughs
    handleUpdateChart() { this.$refs.wordCloud?.updateChart(); },
    updateImageSize(s) { this.imageWidth = s.width; this.imageHeight = s.height; },
    handleMaskSelected(url) { 
        if (url && (url.startsWith('data:') || url.startsWith('http') || url.startsWith('blob:'))) {
           this.selectedMask = url;
        } else {
           // Fallback for legacy local assets
           try {
              this.selectedMask = require(`@/assets/${url}`);
           } catch (e) {
              console.warn("Could not load local mask:", url);
              this.selectedMask = url; // Try raw path as last resort
           }
        }
        this.maskVisible = false; 
    },
    handleMaskFileSelect(file) {
        // ... (File reading logic same as before)
        const reader = new FileReader();
        reader.onload = (e) => {
            this.customMaskDataUrl = e.target.result;
            this.selectedMask = null;
        };
        reader.readAsDataURL(file);
    },
    async exportLayout() {
        if (!this.checkSubscription()) { this.priceVisible = true; return; }
        // Reset zoom effectively handled by component using internal scaling? 
        // Or we temporarily set zoomLevel = 1.
        const oldZoom = this.zoomLevel;
        this.zoomLevel = 1;
        this.$nextTick(async () => {
            await this.$refs.wordCloud?.exportChart();
            this.zoomLevel = oldZoom;
        });
    },
    loadSelectedFont(font) {
        const link = document.createElement('link');
        link.href = `//fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}:wght@400;700&display=swap`;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    },
    changeLang(l) { this.$i18n.locale = l; },
    dismissPricing() {
      // Mark that user dismissed the pricing dialog
      localStorage.setItem('pricingDismissed', 'true');
      this.priceVisible = false;
    }
  }
}
</script>

<style scoped>
/* Transitions for Panels */
.slide-left-enter-active, .slide-left-leave-active,
.slide-right-enter-active, .slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-left-enter-from, .slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-enter-from, .slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
<style>
.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>